import { useTranslation } from 'next-i18next'
// Libs
import { useState, useMemo, useEffect, useRef } from 'react'
import styled from 'styled-components'

// Comps
import { CTA } from '../../CTA'
import { icons, activeWhen, show } from '../../../utils'
import { EmailRsvpHock } from '../../../hooks/emailRsvp'
import { waitingListButtonStyles } from './waitingListButtonStyles'

const ButtonContainer = styled.div`
  ${waitingListButtonStyles}
`

// Defs
interface Props {
  emailRsvpHock: EmailRsvpHock
  onAddToWaitingList: () => void
  onRemoveFromWaitingList: () => void
  // hideLeaveOption?: boolean
  // hideJoinOption?: boolean
}

export const EmailRSVPWaitingListButton: React.FC<Props> = ({
  emailRsvpHock,
  onAddToWaitingList,
  onRemoveFromWaitingList,
  // hideLeaveOption = false, hideJoinOption = false
}) => {
  const { t } = useTranslation('common')

  // Local State
  const [showButtonDropdown, setShowButtonDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Global State
  const {
    currentEvent,
    // removeCurrentWaitingUserFromEvent,
    // addCurrentUserToEventWaitingList
  } = emailRsvpHock
  const { waiting_list, is_user_waiting, hosted_group } = currentEvent || {}

  // Calc
  // Figure out what items need to be in the selected element
  const participantProperty = useMemo(() => {
    if (is_user_waiting !== true) {
      return { color: 'purple', label: 'Join wait list' }
    } else {
      return {
        color: 'selago',
        label: 'On wait list',
        icon: `${icons.checkTickPurple}`,
      }
    }
  }, [currentEvent, waiting_list])

  // Effects
  // When mounted add dismiss option when user click out side the drop down
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowButtonDropdown(false)
      }
    }
    window && window.addEventListener('click', handleClickOutside)
    return () =>
      window && window.removeEventListener('click', handleClickOutside)
  }, [dropdownRef])

  // Funcs
  const onClickOpenDropDown = () => {
    if (is_user_waiting !== true) {
      onAddToWaitingList()
    } else {
      setShowButtonDropdown(true)
    }
  }

  // When the item changes update
  const onOptionClick = async (joinList: boolean) => {
    if (joinList === true) {
      onAddToWaitingList()
    } else {
      onRemoveFromWaitingList()
    }
    setShowButtonDropdown(false)
  }

  // ..
  return (
    <>
      {/* Button. */}
      <ButtonContainer ref={dropdownRef}>
        {/* The option value and Button */}
        <CTA
          className={`book-button ${participantProperty.color}`}
          onClick={onClickOpenDropDown}>
          {show(participantProperty.icon !== undefined) && (
            <>
              <img className="buttonIcon" src={participantProperty.icon} />
            </>
          )}
          {participantProperty.label}
        </CTA>

        {/* The List of Options */}
        {show(showButtonDropdown === true) && (
          <>
            <div className="button-dropdown-container">
              {/* Optional hide join option if not on the list */}
              {/* {show(hideJoinOption === false) && <> */}
              <button
                className={`option-button light ${activeWhen(
                  is_user_waiting,
                  true,
                )}`}
                onClick={() => {
                  onOptionClick(true)
                }}>
                <svg
                  className="option-icon"
                  width="22"
                  height="22"
                  viewBox="0 0 22 22"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M20.1666 10.1567V11C20.1655 12.9768 19.5254 14.9002 18.3418 16.4834C17.1582 18.0666 15.4946 19.2248 13.599 19.7853C11.7034 20.3457 9.67741 20.2784 7.82318 19.5934C5.96896 18.9084 4.38585 17.6423 3.30997 15.984C2.23408 14.3257 1.72306 12.3641 1.85312 10.3917C1.98318 8.41922 2.74736 6.54167 4.03168 5.03902C5.31599 3.53637 7.05164 2.48914 8.97976 2.05351C10.9079 1.61787 12.9252 1.81718 14.7308 2.62171"
                    stroke="#6D4BEA"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M20.1667 3.66669L11 12.8425L8.25 10.0925"
                    stroke="#6D4BEA"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>{t(`On wait list`, `On wait list`)}</span>
                <svg
                  className="circle"
                  width="16"
                  height="16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <circle cx="8" cy="8" r="7.5" />
                  <circle cx="8" cy="8" r="4.5" />
                </svg>
              </button>
              {/* </>} */}

              {/* Optional hide leave option if not on the list */}
              {/* {show(hideLeaveOption === false) && <> */}
              <button
                className={`option-button dark`}
                onClick={() => {
                  onOptionClick(false)
                }}>
                <svg
                  className="option-icon"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                    stroke="#4F4F4F"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M15 9L9 15"
                    stroke="#4F4F4F"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M9 9L15 15"
                    stroke="#4F4F4F"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>

                <span>{t(`Leave waiting List`, `Leave waiting List`)}</span>
                <svg
                  className="circle"
                  width="16"
                  height="16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <circle cx="8" cy="8" r="7.5" />
                  <circle cx="8" cy="8" r="4.5" />
                </svg>
              </button>
              {/* </>} */}
            </div>
          </>
        )}
      </ButtonContainer>
    </>
  )
}
